/**
 * AssessmentHistory Component
 * Displays assessment history table with filters
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import { DataGridPremium, GridColDef, GridSortModel } from "@mui/x-data-grid-premium";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { appDatePickerSlots } from "../../shared/datePickerSlots";
import { Dayjs } from "dayjs";
import { apiClient } from "../../../services/apiClient";
import { getAssessmentReportPresignedUrl } from "../../../services/assessmentService";
import { getAssessmentFilterOptions } from "../../../services/regulationsService";
import {
  formatAssessmentHistoryStatus,
  resolveAssessmentFilterOptionQueryValues,
  resolveAssessmentStatusForPill,
} from "../../../utils/assessmentHistoryStatus";
import { resolveAiImpactAssessmentReportUri } from "../../../utils/manualAssessmentReportShare";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import StartManualAssessmentDialog, {
  type ManualAssessmentEntrySource,
} from "../../AssessmentCenterPage/StartManualAssessmentDialog";
import {
  buildConceptAssessmentResult,
  coerceToTriageImpactedItems,
  type StartManualAssessmentRegulationContext,
  type TriageImpactedItem,
} from "../../../services/manualAssessmentService";
import {
  AiImpactAssessmentActionIcons,
  AiStyleAssessmentStatusCell,
  formatAiAssessmentTypeForDisplay,
  resolveAiAssessmentCenterStatusHoverTitle,
  type AiImpactAssessmentRow,
} from "../../AssessmentCenterPage/assessmentCenterColumns";
import { useAiImpactAssessmentRowActions } from "../../AssessmentCenterPage/useAiImpactAssessmentRowActions";

interface HistoryImpactedItem {
  type?: string;
  keyword?: string | null;
  concept?: {
    name?: string | null;
    code?: string | null;
    description?: string | null;
  } | null;
  concept_value?: {
    name?: string | null;
    code?: string | null;
    description?: string | null;
  } | null;
}

interface AssessmentHistoryApiItem {
  bundle_task_uuid?: string;
  executed_at?: string;
  status?: string;
  executor_name?: string;
  impacted_level?: string | null;
  impact_level?: string | null;
  impacted?: boolean | null;
  report_url?: string | null;
  references?: Array<{
    uri?: string | null;
    name?: string | null;
    content_type?: string | null;
  }> | null;
  by?: string | null;
  impacted_items?: HistoryImpactedItem[] | null;
  failed_concept_names?: string[] | null;
  regulation_id?: string | null;
  regulation_version?: string | null;
  regulation_title?: string | null;
}

interface AssessmentHistoryRow {
  id: string;
  taskUuid: string | null;
  executedTime: string;
  rawStatus: string;
  status: string;
  executedBy: string;
  /** Raw `by` from API (`user` / `system`) — same as Assessment Center AI grid. */
  assessmentType: string;
  impactedConcepts: string;
  impactedItems: HistoryImpactedItem[];
  impact: string;
  impacted: boolean;
  impactedLevelApi: string;
  details: string;
  reportUrl: string | null;
  regulationId: string;
  version: string;
  regulation?: string;
  failedConceptNames?: readonly string[];
}

interface FilterOption {
  name: string;
  value: string | string[];
}

interface FilterConfig {
  name: string;
  display_name: string;
  description: string;
  options: FilterOption[];
}

interface AssessmentHistoryProps {
  onViewDetails: (pdfUrl: string) => void;
  regulationId: string;
  regulationVersion: string;
}

function stripVersionPrefix(version: string): string {
  const t = version.trim();
  return t.startsWith("v") || t.startsWith("V") ? t.slice(1) : t;
}

function historyRowToAiImpactRow(row: AssessmentHistoryRow): AiImpactAssessmentRow {
  return {
    rawStatus: row.rawStatus,
    status: row.status,
    taskUuid: row.taskUuid ?? "",
    regulationId: row.regulationId,
    regulation: row.regulation,
    version: row.version,
    assessmentType: row.assessmentType,
    reportUrl: row.reportUrl,
    failedConceptNames: row.failedConceptNames,
    impactedItems: coerceToTriageImpactedItems(row.impactedItems),
  };
}

export const AssessmentHistory: React.FC<AssessmentHistoryProps> = ({
  onViewDetails,
  regulationId,
  regulationVersion,
}) => {
  const [status, setStatus] = useState("");
  const [executedBy, setExecutedBy] = useState("");
  const [impactLevel, setImpactLevel] = useState("");
  const [date, setDate] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<AssessmentHistoryRow[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterConfig[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [totalRows, setTotalRows] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const sortChangedRef = useRef(false);
  const [startManualDialogOpen, setStartManualDialogOpen] = useState(false);
  const [startManualImpactedItems, setStartManualImpactedItems] = useState<
    readonly TriageImpactedItem[] | undefined
  >(undefined);
  const [startManualRegulation, setStartManualRegulation] = useState<
    StartManualAssessmentRegulationContext | undefined
  >(undefined);
  const [startManualConceptResult, setStartManualConceptResult] = useState<
    ReturnType<typeof buildConceptAssessmentResult> | null
  >(null);
  const [startManualEntrySource, setStartManualEntrySource] =
    useState<ManualAssessmentEntrySource>("direct");

  const canRerunSystem = useHasPrivilege("gri.assessment.impact.system.execute");
  const canStartManualAssessment = useHasPrivilege("gri.assessment.manual.execute");

  const { actionHandlers: aiRowActionHandlers } = useAiImpactAssessmentRowActions({
    canRerunSystem,
    onRetrySuccess: () => setHistoryRefreshKey((value) => value + 1),
  });

  const appendFilterParam = (
    params: URLSearchParams,
    key: "status" | "executed_by" | "impacted_level",
    selectedName: string,
    filterName: string,
  ) => {
    const vals = resolveAssessmentFilterOptionQueryValues(
      filterOptions,
      filterName,
      selectedName,
    );
    if (!vals) return;
    vals.forEach((v) => params.append(key, v));
  };

  const HISTORY_FIELD_MAPPING: Record<string, string> = {
    executedTime: "executed_date",
    executedBy: "executor_name",
    assessmentType: "by",
    impact: "impacted_level",
  };

  const buildHistoryQueryParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    params.append("page", String(paginationModel.page + 1));
    params.append("page_size", String(paginationModel.pageSize));

    if (sortModel.length > 0) {
      const frontendField = sortModel[0].field;
      const backendField = HISTORY_FIELD_MAPPING[frontendField] ?? frontendField;
      params.append("sort_by", backendField);
      params.append("sort_order", sortModel[0].sort ?? "asc");
    }

    if (status && filterOptions.length > 0) {
      appendFilterParam(params, "status", status, "status");
    }

    if (executedBy && filterOptions.length > 0) {
      appendFilterParam(params, "executed_by", executedBy, "executed_by");
    }

    if (impactLevel) {
      if (filterOptions.length > 0) {
        appendFilterParam(params, "impacted_level", impactLevel, "impacted_level");
      } else {
        params.append("impacted_level", impactLevel.toLowerCase());
      }
    }

    if (date) {
      params.append("date", date.format("YYYY-MM-DD"));
    }

    return params;
  };

  const formatImpactedItems = (items: HistoryImpactedItem[] | null | undefined) => {
    if (!items?.length) return "—";
    return items
      .map((item) => {
        if (item.type === "keyword" && item.keyword) return item.keyword;
        if (item.concept_value?.name) return item.concept_value.name;
        if (item.concept?.name) return item.concept.name;
        return item.type;
      })
      .filter((value): value is string => Boolean(value))
      .join(", ") || "—";
  };

  const getImpactLevel = (item: AssessmentHistoryApiItem): string => {
    if (item.impacted_level !== undefined && item.impacted_level !== null) {
      return String(item.impacted_level);
    }
    if (item.impact_level !== undefined && item.impact_level !== null) {
      return String(item.impact_level);
    }
    if (item.impacted !== undefined && item.impacted !== null) {
      return item.impacted ? "High" : "No";
    }
    return "-";
  };

  const transformHistoryRows = (rawData: AssessmentHistoryApiItem[]): AssessmentHistoryRow[] => {
    const versionFallback = stripVersionPrefix(regulationVersion);
    return rawData.map((item, index) => {
      const failedNames = Array.isArray(item.failed_concept_names)
        ? item.failed_concept_names.filter(
            (n): n is string => typeof n === "string" && n.trim().length > 0,
          )
        : [];
      const rid = item.regulation_id?.trim() || regulationId;
      const ver = item.regulation_version?.trim() || versionFallback;
      const title =
        item.regulation_title?.trim() ||
        rid ||
        undefined;
      return {
        id:
          item.bundle_task_uuid ??
          `${regulationId}:${regulationVersion}:${item.executed_at ?? index}`,
        taskUuid: item.bundle_task_uuid ?? null,
        executedTime: formatDateTime(item.executed_at ?? ""),
        rawStatus: item.status || "",
        status: formatAssessmentHistoryStatus(item.status || ""),
        executedBy: item.executor_name || "",
        assessmentType: item.by?.trim() || "—",
        impactedConcepts: formatImpactedItems(item.impacted_items),
        impactedItems: item.impacted_items ?? [],
        impacted: Boolean(item.impacted),
        impactedLevelApi:
          (item.impacted_level ?? item.impact_level ?? "no").trim() || "no",
        impact: getImpactLevel(item),
        details: "View Details",
        reportUrl:
          resolveAiImpactAssessmentReportUri(
            item as Record<string, unknown>,
          ) || null,
        regulationId: rid,
        version: ver,
        regulation: title,
        ...(failedNames.length > 0 ? { failedConceptNames: failedNames } : {}),
      };
    });
  };

  // Format ISO date string to readable format: DD-MMM-YYYY
  const formatDateTime = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const day = date.getDate().toString().padStart(2, "0");
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return isoString;
    }
  };

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        setFilterLoading(true);
        const response = await getAssessmentFilterOptions(regulationId, regulationVersion);

        if (response.status === "success" && response.data) {
          setFilterOptions(response.data as any);
        }
      } catch (err) {
        console.error("Error fetching filter options:", err);
      } finally {
        setFilterLoading(false);
        setFilterOptionsLoaded(true);
      }
    };

    fetchFilterOptions();
  }, [regulationId, regulationVersion]);

  // Fetch assessment history data
  useEffect(() => {
    const fetchAssessmentHistory = async () => {
      if (!regulationId || !regulationVersion || !filterOptionsLoaded) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const queryString = buildHistoryQueryParams().toString();
        const url = `/regulations/${regulationId}/${regulationVersion}/assessments/history${
          queryString ? `?${queryString}` : ""
        }`;

        const response = await apiClient.get(url);

        const rawData = Array.isArray(response.data)
          ? (response.data as AssessmentHistoryApiItem[])
          : [];
        const total =
          (response as unknown as { total?: number })?.total ?? rawData.length;
        if (response.status === "success") {
          setHistoryData(transformHistoryRows(rawData));
          setTotalRows(total);
        } else {
          setHistoryData([]);
          // Preserve totalRows so server pagination does not snap back to page 0.
        }
      } catch (err) {
        console.error("Error fetching assessment history:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load assessment history"
        );
        setHistoryData([]);
        // Preserve totalRows on transport/API errors.
      } finally {
        setLoading(false);
      }
    };

    fetchAssessmentHistory();
  }, [
    regulationId,
    regulationVersion,
    status,
    executedBy,
    impactLevel,
    date,
    filterOptions,
    filterOptionsLoaded,
    paginationModel,
    sortModel,
    historyRefreshKey,
  ]);

  const handleStartManualAssessment = useCallback((row: AssessmentHistoryRow) => {
    setStartManualRegulation({
      regulationId: row.regulationId,
      regulationVersion: row.version,
      regulationTitle: row.regulation || row.regulationId,
    });
    const items = coerceToTriageImpactedItems(row.impactedItems);
    const triageItems = items.length > 0 ? items : undefined;
    setStartManualImpactedItems(triageItems);
    setStartManualConceptResult(
      triageItems
        ? buildConceptAssessmentResult(
            row.regulationId,
            row.version,
            row.impacted,
            row.impactedLevelApi,
            row.reportUrl,
            triageItems,
          )
        : null,
    );
    setStartManualEntrySource("from_ai_impact");
    setStartManualDialogOpen(true);
  }, []);

  const handlePreviewPDF = useCallback(
    async (filePath: string) => {
      try {
        const { presigned_url } = await getAssessmentReportPresignedUrl(filePath);
        onViewDetails(presigned_url);
      } catch (error) {
        console.error("Error previewing PDF:", error);
        setError("Failed to load PDF preview");
      }
    },
    [onViewDetails],
  );

  const actionHandlersBase = useMemo(
    () => ({
      ...aiRowActionHandlers,
      onViewReport: (row: AiImpactAssessmentRow) => {
        const filePath = row.reportUrl;
        if (!filePath) return;
        void handlePreviewPDF(filePath);
      },
    }),
    [aiRowActionHandlers, handlePreviewPDF],
  );

  const historyColumns: GridColDef<AssessmentHistoryRow>[] = [
    {
      field: "executedTime",
      headerName: "Executed time",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const tooltipTitle = resolveAiAssessmentCenterStatusHoverTitle(
          row?.rawStatus,
          row?.status,
          row?.failedConceptNames,
        );
        return (
          <AiStyleAssessmentStatusCell
            rawStatus={resolveAssessmentStatusForPill(
              row?.rawStatus,
              row?.status,
            )}
            tooltipTitle={tooltipTitle}
            contentAlign="top"
          />
        );
      },
    },
    {
      field: "executedBy",
      headerName: "Executed by",
      flex: 1,
      minWidth: 150,
      sortable: false,
    },
    {
      field: "assessmentType",
      headerName: "Assessment Type",
      flex: 1,
      minWidth: 150,
      sortable: true,
      valueFormatter: (value: string) => formatAiAssessmentTypeForDisplay(value),
    },
    {
      field: "impactedConcepts",
      headerName: "Impacted Values",
      flex: 1.2,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "impact",
      headerName: "Impact Level",
      flex: 1,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const value = params.value;
        if (!value || value === "-") {
          return <span>-</span>;
        }

        const capitalized = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
        const impactLevelStyles: Record<string, { backgroundColor: string; color: string }> = {
          high:   { backgroundColor: "#fee2e2", color: "#991b1b" },
          medium: { backgroundColor: "#ffedd5", color: "#9a3412" },
          low:    { backgroundColor: "#fef9c3", color: "#854d0e" },
          no:     { backgroundColor: "#dcfce7", color: "#166534" },
        };

        const style = impactLevelStyles[value.toLowerCase()];
        if (style) {
          return (
            <Chip
              label={capitalized}
              size="small"
              sx={{
                ...style,
                fontWeight: 500,
                fontSize: "0.75rem",
                height: "24px",
                "& .MuiChip-label": {
                  px: 1.5,
                },
              }}
            />
          );
        }

        return <span>{capitalized}</span>;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 176,
      maxWidth: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <AiImpactAssessmentActionIcons
          row={historyRowToAiImpactRow(params.row)}
          contentAlign="top"
          handlers={{
            ...actionHandlersBase,
            onStartManualAssessment: canStartManualAssessment
              ? () => handleStartManualAssessment(params.row)
              : undefined,
          }}
        />
      ),
    },
  ];

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 200 }}
          size="small"
          disabled={loading || filterLoading}
        >
          <MenuItem value="">All</MenuItem>
          {filterOptions
            .find((filter) => filter.name === "status")
            ?.options.map((option) => (
              <MenuItem key={option.name} value={option.name}>
                {option.name}
              </MenuItem>
            ))}
        </TextField>

        <TextField
          select
          label="Executed by"
          value={executedBy}
          onChange={(e) => setExecutedBy(e.target.value)}
          sx={{ minWidth: 200 }}
          size="small"
          disabled={loading || filterLoading}
        >
          <MenuItem value="">All</MenuItem>
          {(filterOptions.find((f) => f.name === "executed_by")?.options ?? []).map((opt) => (
            <MenuItem key={opt.name} value={opt.name}>
              {opt.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Impact Level"
          value={impactLevel}
          onChange={(e) => setImpactLevel(e.target.value)}
          sx={{ minWidth: 200 }}
          size="small"
          disabled={loading || filterLoading}
        >
          <MenuItem value="">All</MenuItem>
          {(filterOptions.find((f) => f.name === "impacted_level")?.options ?? []).length > 0 ? (
            (filterOptions.find((f) => f.name === "impacted_level")?.options ?? []).map((opt) => (
              <MenuItem key={opt.name} value={opt.name}>
                {opt.name}
              </MenuItem>
            ))
          ) : (
            <>
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </>
          )}
        </TextField>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date"
            value={date}
            format="DD-MMM-YYYY"
            onChange={(newValue) => setDate(newValue)}
            disabled={loading}
            slots={appDatePickerSlots}
            slotProps={{
              textField: {
                size: "small",
                sx: { minWidth: 200 },
              },
              field: { clearable: true, onClear: () => setDate(null) },
            }}
          />
        </LocalizationProvider>
      </Box>

      {/* Assessment History table */}
      <DataGridPremium
        rows={historyData}
        columns={historyColumns}
        loading={loading}
        getRowId={(row) => row.id}
        autoHeight
        pagination
        paginationMode="server"
        rowCount={totalRows}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          setLoading(true);
          if (sortChangedRef.current) {
            sortChangedRef.current = false;
            if (newModel.page === 0 && paginationModel.page !== 0) {
              setPaginationModel((prev) => ({ ...prev, pageSize: newModel.pageSize }));
              return;
            }
          }
          setPaginationModel(newModel);
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          sortChangedRef.current = true;
          setSortModel(newSortModel);
        }}
        disableRowSelectionOnClick
        disableColumnMenu
        pageSizeOptions={[10, 20, 50, 100]}
        getRowHeight={() => 'auto'}
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
            fontSize: (theme) => theme.typography.body2.fontSize,
            color: "#374151",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #f3f4f6",
            fontSize: (theme) => theme.typography.body2.fontSize,
            py: 1.5,
            alignItems: "flex-start",
            lineHeight: "normal",
            whiteSpace: "normal",
          },
          "& .MuiDataGrid-cellContent": {
            lineHeight: "normal",
            whiteSpace: "normal",
          },
          "& .MuiDataGrid-row": {
            minHeight: "52px !important",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#f9fafb",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "1px solid #e5e7eb",
          },
        }}
      />

      <StartManualAssessmentDialog
        open={startManualDialogOpen}
        regulation={
          startManualRegulation ?? {
            regulationId,
            regulationVersion: stripVersionPrefix(regulationVersion),
            regulationTitle: regulationId,
          }
        }
        onClose={() => {
          setStartManualDialogOpen(false);
          setStartManualImpactedItems(undefined);
          setStartManualRegulation(undefined);
          setStartManualConceptResult(null);
          setStartManualEntrySource("direct");
        }}
        entrySource={startManualEntrySource}
        impactedItems={startManualImpactedItems}
        conceptAssessmentResult={startManualConceptResult}
        onInitiated={() => {
          setHistoryRefreshKey((k) => k + 1);
        }}
      />
    </Box>
  );
};
